"use client"

import { createContext, useContext, useState, useCallback, useMemo, type ReactNode } from "react"
import { useRouter, usePathname } from "next/navigation"

// ===== Types =====

export interface RunContextValue {
  // Current run ID
  runId: string | null
  setRunId: (id: string | null) => void
  
  // Run mode (database ETL vs codebase scan)
  mode: "database" | "codebase"
  setMode: (mode: "database" | "codebase") => void
  
  // Navigation helper - sets runId and updates URL for bookmarking
  navigateToRun: (runId: string, runMode: "database" | "codebase") => void
  
  // Sidebar state (persists across run switches)
  sidebarExpanded: boolean
  setSidebarExpanded: (expanded: boolean) => void
  toggleSidebar: () => void
}

// ===== Context =====

const RunContext = createContext<RunContextValue | null>(null)

// ===== Provider =====

export function RunProvider({ 
  children,
  initialRunId = null,
  initialMode = "codebase",
}: { 
  children: ReactNode
  initialRunId?: string | null
  initialMode?: "database" | "codebase"
}) {
  const router = useRouter()
  const pathname = usePathname()
  const [runId, setRunIdState] = useState<string | null>(initialRunId)
  const [mode, setModeState] = useState<"database" | "codebase">(initialMode)
  const [sidebarExpanded, setSidebarExpanded] = useState(true)

  // Set runId and optionally update browser history for bookmarking
  const setRunId = useCallback((id: string | null) => {
    setRunIdState(id)
  }, [])

  // Set mode
  const setMode = useCallback((newMode: "database" | "codebase") => {
    setModeState(newMode)
  }, [])

  // Navigate to a run - updates context AND URL (for bookmarking)
  const navigateToRun = useCallback((newRunId: string, runMode: "database" | "codebase") => {
    console.log('[RunContext] navigateToRun called:', { newRunId, runMode, currentRunId: runId, pathname })

    // Update context state
    setRunIdState(newRunId)
    setModeState(runMode)

    // Simple URL without mode path - mode comes from context/API
    const newUrl = `/workflow/${newRunId}`

    console.log('[RunContext] Updating URL to:', newUrl)

    // Use router.replace so Next.js re-renders the layout with the new
    // workflowId param (window.history.replaceState skips Next.js entirely,
    // leaving WorkflowStatusProvider on the old workflow's data).
    if (pathname.startsWith('/workflow/')) {
      router.replace(newUrl)
    } else {
      router.push(newUrl)
    }
  }, [pathname, router])

  // Toggle sidebar
  const toggleSidebar = useCallback(() => {
    setSidebarExpanded(prev => !prev)
  }, [])

  // PERF: Memoize context value to prevent unnecessary re-renders of consumers
  // Only re-creates when actual values change, not on every provider render
  const value = useMemo<RunContextValue>(() => ({
    runId,
    setRunId,
    mode,
    setMode,
    navigateToRun,
    sidebarExpanded,
    setSidebarExpanded,
    toggleSidebar,
  }), [runId, setRunId, mode, setMode, navigateToRun, sidebarExpanded, setSidebarExpanded, toggleSidebar])

  return (
    <RunContext.Provider value={value}>
      {children}
    </RunContext.Provider>
  )
}

// ===== Hook =====

export function useRunContext(): RunContextValue {
  const context = useContext(RunContext)
  if (!context) {
    throw new Error("useRunContext must be used within a RunProvider")
  }
  return context
}

// Optional: Hook that returns null instead of throwing (for optional usage)
export function useRunContextOptional(): RunContextValue | null {
  return useContext(RunContext)
}
